import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useUserAccess } from '../hooks/useUserAccess';
import { useDreAccess } from '../hooks/useDreAccess';
import DocViewer from '../components/DocViewer';
import {
  IconDoc, IconCalendar, IconArrowRight, IconSearch,
  IconChart, IconUpload, IconGear
} from '../components/Icons';
import './Home.css';

const fmtSize = (bytes) => {
  if (!bytes) return '—';
  const kb = bytes / 1024;
  return kb < 1024 ? `${kb.toFixed(1)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

const fmtDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  });
};

/**
 * Home (Fase D / CR-5 + Ajustes): o usuário cai direto nos arquivos que já tem
 * acesso. Sem saudação — uma única divisória "Documentos e relatórios
 * disponíveis" e uma grade única, cada card com uma tag discreta de área
 * (Meus/Diretoria/ADMCEN/Regional). O servidor é a fonte de verdade: áreas que
 * retornam erro são silenciosamente omitidas. Áreas que exigem seleção/navegação
 * (regional c/ seletor, DRE, admin...) viram atalhos em "Outras áreas".
 */
function Home() {
  const access = useUserAccess();
  const { isGerente: isDreGerente } = useDreAccess();
  const [areas, setAreas] = useState([]);      // [{key,tag,container,files}]
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null); // {container,file,name,areaLabel}

  // A regional é listada inline para quem tem acesso de leitura ao container
  // (req_ajuste_03, item 1): escopo concreto traz só a sua pasta; admin/diretor/
  // 'ALL' trazem todas as regionais (o backend achata e devolve a pasta em cada
  // arquivo). `regionalConcrete` ainda decide o atalho de navegação abaixo.
  const regionalConcrete = access.isRegional && access.regionalScope && access.regionalScope !== 'ALL';

  // Áreas candidatas a listagem inline (container + tag de área)
  const inlineAreas = useMemo(() => {
    if (access.loading) return null;
    return [
      { key: 'personal',  container: 'docs',            tag: 'Meus',      enabled: true },
      { key: 'diretoria', container: 'docs-diretoria',  tag: 'Diretoria', enabled: access.isAdmin || access.isDirector },
      { key: 'admcen',    container: 'docs-admcen',     tag: 'ADMCEN',    enabled: access.canAccessAdmcen },
      { key: 'regional',  container: 'docs-gerenciais', tag: access.regionalScope || 'Regional', enabled: access.canAccessRegional },
    ].filter(a => a.enabled);
  }, [access]);

  useEffect(() => {
    if (!inlineAreas) return;
    let cancelled = false;
    setLoadingDocs(true);

    Promise.all(
      inlineAreas.map(async (a) => {
        try {
          const res = await fetch(`/api/listaDocumentos?container=${encodeURIComponent(a.container)}`);
          const data = await res.json();
          if (data.error || !Array.isArray(data.files)) return null;
          return { ...a, files: data.files };
        } catch {
          return null;
        }
      })
    ).then((results) => {
      if (cancelled) return;
      setAreas(results.filter(Boolean).filter((a) => a.files.length > 0));
      setLoadingDocs(false);
    });

    return () => { cancelled = true; };
  }, [inlineAreas]);

  // Atalhos para áreas que não são listadas inline na home
  const shortcuts = useMemo(() => {
    if (access.loading) return [];
    const list = [];
    if ((access.isAdmin || access.isDirector || (access.isRegional && access.regionalScope === 'ALL')) && !regionalConcrete) {
      list.push({ to: '/regional', icon: <IconChart />, name: 'Regional', desc: 'Documentos gerenciais por regional' });
    }
    if (isDreGerente) list.push({ to: '/dre', icon: <IconChart />, name: 'DRE', desc: 'Resultados, justificativas e plano de ação' });
    if (access.canReadDreGlobal) list.push({ to: '/dre-consolidado', icon: <IconChart />, name: 'DRE Consolidado', desc: 'Visão consolidada por região' });
    if (access.canUpload) list.push({ to: '/upload', icon: <IconUpload />, name: 'Enviar Documentos', desc: 'Upload de documentos' });
    if (access.isAdmin) list.push({ to: '/admin', icon: <IconGear />, name: 'Gestão de Acessos', desc: 'Admins e compartilhadores' });
    return list;
  }, [access, regionalConcrete, isDreGerente]);

  // Grade única — todos os arquivos das áreas acessíveis num só lugar,
  // cada card carrega a tag da sua área.
  const allFiles = useMemo(() => {
    const out = [];
    areas.forEach((a) => {
      a.files.forEach((f) => out.push({
        container: a.container,
        // Regional em escopo 'ALL' devolve a pasta por arquivo (f.location) —
        // usa como tag do card; senão cai na tag da área (ex.: 'Regional Pr 01').
        tag: (a.key === 'regional' && f.location) ? f.location : a.tag,
        name: f.name,
        fullPath: f.fullPath || f.name,
        size: f.size,
        lastModified: f.lastModified
      }));
    });
    return out;
  }, [areas]);

  const q = query.trim().toLowerCase();
  const filteredFiles = useMemo(() => {
    if (!q) return allFiles;
    return allFiles.filter((f) => f.name.toLowerCase().includes(q));
  }, [allFiles, q]);

  if (access.loading || loadingDocs) {
    return <div className="home"><p className="home-loading">Carregando seus documentos...</p></div>;
  }

  return (
    <div className="home">
      <section className="home-section">
        <div className="section-label">
          <span>Documentos e relatórios disponíveis</span>
          {allFiles.length > 0 && (
            <span className="doc-count">
              {filteredFiles.length} {filteredFiles.length === 1 ? 'relatório' : 'relatórios'}
            </span>
          )}
        </div>

        {allFiles.length > 0 && (
          <label className="home-search" aria-label="Buscar documentos">
            <IconSearch />
            <input
              type="search"
              placeholder="Pesquisar documento por nome…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoComplete="off"
            />
            {query && (
              <button
                type="button"
                className="home-search-clear"
                onClick={() => setQuery('')}
                aria-label="Limpar busca"
              >
                ×
              </button>
            )}
          </label>
        )}

        {allFiles.length === 0 ? (
          <div className="home-empty">
            <p>Nenhum documento disponível para o seu acesso ainda.</p>
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="home-empty"><p>Nenhum documento encontrado para “{query}”.</p></div>
        ) : (
          <div className="doc-grid">
            {filteredFiles.map((file, i) => (
              <article
                key={`${file.container}/${file.fullPath}/${i}`}
                className="doc-card"
                tabIndex={0}
                role="button"
                aria-label={`Abrir ${file.name}`}
                onClick={() => setSelected({ container: file.container, file: file.fullPath, name: file.name, areaLabel: file.tag })}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelected({ container: file.container, file: file.fullPath, name: file.name, areaLabel: file.tag });
                  }
                }}
              >
                <div className="doc-card-icon"><IconDoc /></div>
                <div className="doc-card-info">
                  <h3 className="doc-card-name" title={file.name}>{file.name}</h3>
                  <span className="doc-card-meta">
                    <span className="doc-card-tag">{file.tag}</span>
                    <IconCalendar /> {fmtDate(file.lastModified)} • {fmtSize(file.size)}
                  </span>
                </div>
                <span className="doc-card-cta">Abrir <IconArrowRight /></span>
              </article>
            ))}
          </div>
        )}
      </section>

      {shortcuts.length > 0 && (
        <section className="home-section">
          <div className="section-label"><span>Outras áreas</span></div>
          <div className="actions-grid">
            {shortcuts.map((s) => (
              <Link to={s.to} className="action-card" key={s.to}>
                <span className="action-icon">{s.icon}</span>
                <div className="action-info">
                  <span className="action-name">{s.name}</span>
                  <span className="action-desc">{s.desc}</span>
                </div>
                <span className="action-arrow"><IconArrowRight /></span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {selected && (
        <DocViewer
          container={selected.container}
          file={selected.file}
          title={selected.name}
          subtitle={selected.areaLabel}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

export default Home;
